import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
    Box,
    Dialog,
    IconButton,
    Typography,
    useTheme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import CloseIcon from '@material-ui/icons/Close'
import RemoveIcon from '@material-ui/icons/Remove'

interface Props {
    src: string
    alt?: string
    thumbStyle?: React.CSSProperties
    children?: React.ReactNode
}

const MIN_SCALE = 1
const MAX_SCALE = 4
const STEP = 0.25

const containSize = (
    naturalW: number,
    naturalH: number,
    paneW: number,
    paneH: number,
) => {
    if (naturalW <= 0 || naturalH <= 0 || paneW <= 0 || paneH <= 0) {
        return { w: paneW, h: paneH }
    }
    const ratio = Math.min(paneW / naturalW, paneH / naturalH)
    return {
        w: Math.max(1, Math.floor(naturalW * ratio)),
        h: Math.max(1, Math.floor(naturalH * ratio)),
    }
}

export const FloorplanPreview = (props: Props) => {
    const { src, alt = 'Floor plan', thumbStyle, children } = props
    const [open, setOpen] = useState(false)
    const [scale, setScale] = useState(MIN_SCALE)
    const [natural, setNatural] = useState<{ w: number; h: number } | null>(
        null,
    )
    const [fit, setFit] = useState<{ w: number; h: number }>({ w: 0, h: 0 })
    const paneRef = useRef<HTMLDivElement>(null)

    const close = () => {
        setOpen(false)
        setScale(MIN_SCALE)
    }

    const setClampedScale = (next: number) => {
        const rounded = Math.round(next * 100) / 100
        setScale(Math.min(MAX_SCALE, Math.max(MIN_SCALE, rounded)))
    }

    useLayoutEffect(() => {
        if (!open || !natural) {
            return
        }
        const measure = () => {
            const pane = paneRef.current
            if (!pane) {
                return
            }
            setFit(
                containSize(
                    natural.w,
                    natural.h,
                    pane.clientWidth,
                    pane.clientHeight,
                ),
            )
        }
        measure()
        window.addEventListener('resize', measure)
        return () => window.removeEventListener('resize', measure)
    }, [open, natural])

    useEffect(() => {
        const el = paneRef.current
        if (!open || !el) {
            return
        }
        const onWheel = (event: WheelEvent) => {
            event.preventDefault()
            setScale((prev) => {
                const next = prev + (event.deltaY < 0 ? STEP : -STEP)
                return Math.min(
                    MAX_SCALE,
                    Math.max(MIN_SCALE, Math.round(next * 100) / 100),
                )
            })
        }
        el.addEventListener('wheel', onWheel, { passive: false })
        return () => el.removeEventListener('wheel', onWheel)
    }, [open])

    const displayW = Math.round(fit.w * scale)
    const displayH = Math.round(fit.h * scale)

    return (
        <>
            {children ? (
                <span
                    onClick={() => setOpen(true)}
                    style={{ cursor: 'zoom-in', display: 'inline-block' }}
                >
                    {children}
                </span>
            ) : (
                <img
                    src={src}
                    alt={alt}
                    onClick={() => setOpen(true)}
                    title="Click to expand"
                    style={{
                        cursor: 'zoom-in',
                        display: 'block',
                        ...thumbStyle,
                    }}
                />
            )}
            <Dialog
                open={open}
                onClose={close}
                maxWidth={false}
                PaperProps={{
                    style: {
                        background: '#111',
                        width: '92vw',
                        height: '92vh',
                        maxWidth: '92vw',
                        maxHeight: '92vh',
                        margin: 16,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    style={{
                        gap: 4,
                        padding: '8px 12px',
                        background: '#1c1c1c',
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        style={{ color: '#fff', flex: 1, fontWeight: 600 }}
                        noWrap
                    >
                        {alt}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => setClampedScale(scale - STEP)}
                        disabled={scale <= MIN_SCALE}
                        style={{ color: '#fff' }}
                    >
                        <RemoveIcon />
                    </IconButton>
                    <Typography
                        variant="caption"
                        style={{
                            color: '#fff',
                            minWidth: 40,
                            textAlign: 'center',
                        }}
                    >
                        {Math.round(scale * 100)}%
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={() => setClampedScale(scale + STEP)}
                        disabled={scale >= MAX_SCALE}
                        style={{ color: '#fff' }}
                    >
                        <AddIcon />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={close}
                        style={{ color: '#fff' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
                <div
                    ref={paneRef}
                    style={{
                        flex: 1,
                        minHeight: 0,
                        overflow: scale > MIN_SCALE ? 'auto' : 'hidden',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: scale > MIN_SCALE ? 'flex-start' : 'center',
                    }}
                >
                    <img
                        src={src}
                        alt={alt}
                        onLoad={(event) => {
                            setNatural({
                                w: event.currentTarget.naturalWidth,
                                h: event.currentTarget.naturalHeight,
                            })
                        }}
                        style={{
                            width: displayW || 'auto',
                            height: displayH || 'auto',
                            maxWidth: displayW ? undefined : '100%',
                            maxHeight: displayH ? undefined : '100%',
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                </div>
            </Dialog>
        </>
    )
}

interface ThumbProps {
    src?: string | null
    alt: string
    thumbStyle?: React.CSSProperties
}

export const FloorplanThumb = (props: ThumbProps) => {
    const { src, alt, thumbStyle } = props
    const theme = useTheme()
    if (src) {
        return <FloorplanPreview src={src} alt={alt} thumbStyle={thumbStyle} />
    }
    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            style={{
                ...thumbStyle,
                boxSizing: 'border-box',
                background: theme.palette.grey[100],
                border: `1px dashed ${theme.palette.grey[400]}`,
                color: theme.palette.grey[600],
                textAlign: 'center',
                padding: 4,
                flexShrink: 0,
            }}
        >
            <Typography
                variant="caption"
                style={{ lineHeight: 1.2, fontSize: 11 }}
            >
                No image available
            </Typography>
        </Box>
    )
}
